/**
 * The record of what a student actually did.
 *
 * The project commits to independent evaluation of learning gains, diagnostic
 * transfer, and consequential error. All three are claims about REASONING, not
 * about final answers, and none of them can be evaluated from a score alone -
 * you need the trail: which readings they took before committing, whether they
 * ruled out the lookalikes, how many wafers they scrapped while deciding.
 *
 * The environment already generates every one of those events and was throwing
 * them away. This keeps them, and exports something an instructor can read.
 */

import type { ChamberAspect, FaultId, MaintenanceAction } from '../sim/chamber'
import { t, tOr } from '../i18n/i18n'
import { stationSummary, UNATTRIBUTED } from './shift'

/**
 * Fault and aspect ids are plain strings rather than the etch unions they
 * started as.
 *
 * Every tool in the fab now runs shifts - the ALD tool has a heater and a
 * foreline, the scanner has a source and a bake plate - and none of their
 * aspects are chamber aspects. Widening here rather than making the journal
 * generic keeps one record per session across six different tools, which is
 * the point: an instructor reading it wants the student's whole afternoon in
 * order, not six separate logs.
 */
export type JournalEvent =
  | { kind: 'shiftStart'; fault: FaultId | string; complaint: string; tool?: string; toolId?: string; at: number }
  /** `hours` is the shift time an earned reading cost, where it cost any. */
  | { kind: 'reading'; what: string; hours?: number; tool?: string; toolId?: string; readingId?: string; at: number }
  /**
   * The student opened a tool. Display-free on purpose: `tool` here is the
   * stable layout id, because the work-order evaluator matches on it and a
   * translated label would make evidence depend on the session's language.
   */
  | { kind: 'visit'; tool: string; at: number }
  /** The student went somewhere that is not a tool - the sub-fab, for now. */
  | { kind: 'place'; where: 'subfab'; at: number }
  /**
   * A work order completed. Recorded so the evidence is IN the record an
   * instructor reads - completion that lived only in a UI store would vanish
   * from the one document a student can hand in.
   */
  | { kind: 'orderComplete'; order: string; title: string; hours: number; at: number }
  | { kind: 'wafer'; rateNmMin: number; nonUniformityPct: number; inSpec: boolean; at: number }
  /**
   * A unit of product finished at a tool, whatever that unit is.
   *
   * The `wafer` event above is etch-shaped - it records a rate and a
   * non-uniformity, which are not things a wire bonder or a tester produces. A
   * tool whose product is a die or a package could not record itself at all
   * until this existed, and that was the last thing blocking the back end.
   *
   * Deliberately generic: a label for the measurement, its value and unit, and
   * whether it passed. What a "unit" is belongs to the tool.
   */
  | {
      kind: 'product'
      tool: string
      /** wafer, die, package, part. */
      unit: string
      /** How many of them. */
      count: number
      metric: string
      value: number
      valueUnit: string
      inSpec: boolean
      at: number
    }
  | { kind: 'recipeChange'; knob: string; from: number; to: number; at: number }
  | { kind: 'question'; text: string; at: number }
  /**
   * A call the student made BEFORE the tool reported, beside what it then
   * measured (src/talk/predict.ts).
   *
   * The only event here that records what the student THOUGHT rather than
   * what they did, and it earns its place by being paired: it is written
   * once, when the report lands, so there is never a claim in the record
   * without the observation that met it.
   *
   * NO VERDICT. There is no `right` field and nothing computes one. Matching
   * a sentence of prose against a finding is a grade with nobody accountable
   * for it, and it would be invisible when wrong. The pair is the record;
   * the person who says whether it matched is the technician, out loud, from
   * two facts she can see - the same line `work.ts` draws between what
   * happened and why.
   */
  | { kind: 'prediction'; about: string; said: string; measured: string; tool?: string; toolId?: string; at: number }
  /**
   * What they said when the technician asked WHY it went that way - the third
   * beat of predict, observe, explain (src/talk/predict.ts, TALK.md's
   * "Asking why").
   *
   * The more valuable half of the pair and the one that was being thrown
   * away: a direction guess is a guess, but a stated mechanism is a mental
   * model, in the student's own words, and it is the thing an instructor most
   * wants to read. It went into `talkLog` filed as a QUESTION - because
   * every student line goes through the same box - and never into the record.
   *
   * `said` is what they said WHEN ASKED, which is not always an explanation:
   * a student who changes the subject has still said something, and what the
   * record keeps is the utterance rather than a judgement about whether it
   * answered. Every label on it, in the debrief and in the brief, says "when
   * asked why" for that reason.
   *
   * Paired with the `prediction` before it by ORDER, not by matching `about`
   * - they are recorded one after the other and two runs can ask the same
   * thing. See `callsMade` in learn/shift.ts.
   */
  | { kind: 'explanation'; about: string; said: string; tool?: string; toolId?: string; at: number }
  /**
   * `hours` is the tool availability the action costs, where it costs any, and
   * `label` its name in the reader's language - the action id is a stable key
   * for analysis, not something to put in front of a student.
   */
  | {
      kind: 'maintenance'
      action: MaintenanceAction | string
      label?: string
      hours?: number
      tool?: string
      at: number
    }
  /**
   * A wafer moved from one tool to the next carrying state.
   *
   * The event that makes a flow legible. Without it the journal reads as a set
   * of independent tool sessions, which is exactly what the fab was before the
   * wafer could travel.
   */
  | { kind: 'handoff'; from: string; to: string; summary: string; at: number }
  /**
   * A decision taken on the LINE rather than at a tool: which policy the
   * student chose for the floor while the chiller was down, at what load and
   * with the chambers back after how many hours. The one journal event that
   * belongs to no tool, because the decision does not - see sim/line.
   */
  | { kind: 'lineDecision'; scenario?: string; policy: string; load: number; restoreH?: number; at: number }
  | {
      kind: 'call'
      aspect: ChamberAspect | string
      label: string
      correct: boolean
      truth: ChamberAspect | string | null
      /** Stable model id of the tool the call was made at, for the evaluator. */
      toolId?: string
      readingsTaken: number
      wafersRun: number
      questionsAsked: number
      tool?: string
      /**
       * Downtime the call itself bought: the repair that was ordered, plus the
       * real one on top of it where the call was wrong. Readings and preventive
       * maintenance are charged on their own events, not folded in here.
       */
      hoursSpent?: number
      /**
       * What to call that charge on a receipt.
       *
       * A wrong call is two repairs under one number, so naming the line after
       * the aspect the student picked would understate it. The module knows
       * which repairs actually happened; the journal just carries the words.
       */
      repairLabel?: string
      at: number
    }

/** Spec window a wafer has to land in to count as good product. */
export const SPEC = { rateLoNmMin: 160, rateHiNmMin: 260, maxNonUniformityPct: 8 }

export const waferInSpec = (rate: number, nu: number) =>
  rate >= SPEC.rateLoNmMin && rate <= SPEC.rateHiNmMin && nu <= SPEC.maxNonUniformityPct

/**
 * Omit that distributes over a union. A plain `Omit<JournalEvent, 'at'>`
 * collapses the union to its common keys, which would silently reject every
 * event's own fields.
 */
type Recordable<T> = T extends unknown ? Omit<T, 'at'> & { at?: number } : never

export class Journal {
  readonly events: JournalEvent[] = []
  private readonly listeners = new Set<() => void>()

  record(e: Recordable<JournalEvent>): void {
    this.events.push({ ...e, at: e.at ?? Date.now() } as JournalEvent)
    for (const listener of [...this.listeners]) listener()
  }

  /**
   * Bulk-load a previous session's events, notifying once at the end.
   *
   * Restoring through record() would fire every listener per event - a
   * four-thousand-event session would rebuild the performance block four
   * thousand times before the student saw anything.
   */
  restore(events: JournalEvent[]): void {
    this.events.push(...events)
    for (const listener of [...this.listeners]) listener()
  }

  /**
   * Watch for anything being recorded.
   *
   * This exists so the performance block can keep itself current. The
   * alternative was for each of twenty panels to remember to redraw it after
   * every action that records something, which is twenty chances to forget -
   * and a KPI that silently stops updating is worse than no KPI, because it
   * still looks like an answer.
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /** Events since the current shift began - the scope a single call is judged on. */
  sinceShiftStart(): JournalEvent[] {
    const i = this.events.map((e) => e.kind).lastIndexOf('shiftStart')
    return i < 0 ? this.events : this.events.slice(i)
  }

  count(kind: JournalEvent['kind']): number {
    return this.sinceShiftStart().filter((e) => e.kind === kind).length
  }

  /** Wafers run this shift that would not have shipped. */
  scrappedWafers(): number {
    return this.sinceShiftStart().filter((e) => e.kind === 'wafer' && !e.inSpec).length
  }

  clear(): void {
    this.events.length = 0
  }

  /**
   * Per-tool totals, at the head of the record.
   *
   * The narrative below it is what shows REASONING, and that is still the point
   * of this export. But an instructor with thirty of these to read needs to know
   * where to look before they start reading, and counting OUT OF SPEC lines by
   * hand is not that.
   *
   * Padded rather than piped into a markdown table: this is displayed in a
   * <pre>, so the alignment IS the formatting.
   */
  private summaryTable(): string[] {
    const rows = stationSummary(this)
    if (rows.length === 0) return []

    const dash = t('journal.NotApplicable')
    const hours = (h: number) => (h % 1 === 0 ? String(h) : h.toFixed(1)) + ' h'

    const body = rows
      .sort((a, b) => b.runs - a.runs || a.tool.localeCompare(b.tool))
      .map((r) => [
        r.tool === UNATTRIBUTED ? t('journal.Unattributed') : r.tool,
        String(r.runs),
        String(r.outOfSpec),
        r.hours > 0 ? hours(r.hours) : dash,
        r.calls > 0 ? t('journal.CallsRight', { correct: r.correct, calls: r.calls }) : dash,
      ])

    const head = [
      t('journal.ColTool'),
      t('journal.ColRuns'),
      t('journal.ColOutOfSpec'),
      t('journal.ColDowntime'),
      t('journal.ColCalls'),
    ]

    // Widths from the content, so a long tool label never wraps a row.
    const w = head.map((h, i) => Math.max(h.length, ...body.map((r) => r[i].length)))
    const line = (cells: string[]) =>
      cells.map((c, i) => (i === 0 ? c.padEnd(w[i]) : c.padStart(w[i]))).join('  ').trimEnd()

    return [
      '## ' + t('journal.ByStation'),
      '',
      line(head),
      w.map((n) => '-'.repeat(n)).join('  '),
      ...body.map(line),
    ]
  }

  /**
   * Markdown for the instructor. Deliberately a narrative of decisions rather
   * than a score: whether the call was right matters less than whether the
   * evidence was gathered before it was made.
   */
  export(): string {
    const lines: string[] = [t('journal.Title'), '']
    if (this.events.length === 0) {
      lines.push(t('journal.NoActivity'))
      return lines.join('\n')
    }

    lines.push(...this.summaryTable(), '')

    const t0 = this.events[0].at
    const stamp = (at: number) => {
      const s = Math.round((at - t0) / 1000)
      return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
    }

    for (const e of this.events) {
      switch (e.kind) {
        case 'shiftStart':
          lines.push('', `## ${stamp(e.at)} — ${t('journal.ShiftStart')}`, '', `> ${e.complaint}`, '')
          break
        case 'reading':
          lines.push(`- ${stamp(e.at)} ${t('journal.TookReading')}: **${e.what}**`)
          break
        case 'wafer':
          lines.push(
            `- ${stamp(e.at)} ${t('journal.RanWafer')}: ${e.rateNmMin.toFixed(0)} nm/min, ` +
              `${e.nonUniformityPct.toFixed(1)}% NU — ` +
              `${e.inSpec ? t('journal.InSpec') : t('journal.OutOfSpec')}`,
          )
          break
        case 'recipeChange':
          lines.push(`- ${stamp(e.at)} ${t('journal.Changed')} ${e.knob}: ${e.from} → ${e.to}`)
          break
        case 'question':
          lines.push(`- ${stamp(e.at)} ${t('journal.Asked')}: _"${e.text}"_`)
          break
        case 'maintenance':
          lines.push(`- ${stamp(e.at)} ${t('journal.Maintenance')}: ${e.label ?? e.action}`)
          break
        // A handoff is the only event that names two tools, and that is the
        // point of it: an instructor reading the log can see where a wafer went
        // and therefore where a problem could have come from.
        case 'product':
          lines.push(
            `- ${stamp(e.at)} ${e.tool}: ${e.count} ${e.unit} — ` +
              `${e.metric} ${e.value.toFixed(2)} ${e.valueUnit} ` +
              `(${e.inSpec ? 'in spec' : 'OUT OF SPEC'})`,
          )
          break
        case 'handoff':
          lines.push(`- ${stamp(e.at)} ${e.from} → ${e.to}: ${e.summary}`)
          break
        case 'orderComplete':
          lines.push(
            '',
            `### ${stamp(e.at)} — ${tOr('journal.OrderComplete', 'Work order complete')}: ` +
              `${e.title} (${e.hours.toFixed(1)} h)`,
            '',
          )
          break
        case 'call':
          lines.push(
            '',
            `### ${stamp(e.at)} — ${t('journal.CalledIt')}: ${e.label} — ` +
              `${e.correct ? t('journal.Correct') : t('journal.Incorrect')}`,
            '',
            `- ${t('journal.ReadingsBefore')}: ${e.readingsTaken}`,
            `- ${t('journal.WafersRun')}: ${e.wafersRun}`,
            `- ${t('journal.QuestionsAsked')}: ${e.questionsAsked}`,
            e.correct
              ? ''
              : `- ${t('journal.ActualCause')} **${e.truth ?? t('journal.NoFault')}**`,
            '',
          )
          break
      }
    }
    return lines.filter((l) => l !== undefined).join('\n')
  }
}
